/**
 * 닉네임 트렁케이트 — docs/DESIGN.md §3.6.1.
 *
 * - 최대 20 grapheme 표시.
 * - 21 이상: {first10}…{last6}. "…"은 horizontal ellipsis U+2026.
 * - 이모지 등 grapheme cluster를 고려하기 위해 Intl.Segmenter 사용.
 * - 빈 문자열은 "익명" 으로 치환.
 */
const MAX_LEN = 20;
const HEAD = 10;
const TAIL = 6;
const ELLIPSIS = '…';

function segments(s: string): string[] {
  const seg = new Intl.Segmenter('ko', { granularity: 'grapheme' });
  return Array.from(seg.segment(s), (x) => x.segment);
}

export function truncateNickname(input: string): string {
  const normalized = input.normalize('NFC').trim();
  if (normalized.length === 0) return '익명';
  const g = segments(normalized);
  if (g.length <= MAX_LEN) return normalized;
  return g.slice(0, HEAD).join('') + ELLIPSIS + g.slice(-TAIL).join('');
}

/**
 * 메시지 트렁케이트 — docs/DESIGN.md §3.6.2.
 *
 * - 최대 200자까지 표시. 초과 시 끝에 ellipsis.
 * - 개행 문자: 연속 3개 이상은 1개로 병합, 최대 2개까지 보존.
 * - 자소 분리 공격 방지용 NFC 정규화.
 * - URL 자동 감지는 이 단계가 아닌 렌더 단계에서 처리 (순수 텍스트 정제만).
 */
const MAX_MSG = 200;

export function sanitizeMessage(input: string): string {
  let s = input.normalize('NFC');
  // 개행 정리: 3개 이상 → 1개
  s = s.replace(/\n{3,}/g, '\n');
  // 2개 초과는 없도록 추가 클램프
  s = s.replace(/\n{2,}/g, '\n\n');
  if (s.length > MAX_MSG) s = s.slice(0, MAX_MSG) + ELLIPSIS;
  return s;
}

/**
 * 메시지 텍스트에서 URL처럼 보이는 토큰을 감지해 "언링크 + 강조" 대상 범위를 반환.
 * 위젯 렌더는 안전성을 위해 anchor 태그를 만들지 않고 span + accent color만 적용.
 */
export interface TextSegment {
  readonly kind: 'text' | 'url';
  readonly value: string;
}
const URL_RE = /\bhttps?:\/\/[^\s]+/gi;

export function segmentMessage(input: string): TextSegment[] {
  const result: TextSegment[] = [];
  let lastIndex = 0;
  for (const m of input.matchAll(URL_RE)) {
    const idx = m.index ?? 0;
    if (idx > lastIndex) result.push({ kind: 'text', value: input.slice(lastIndex, idx) });
    result.push({ kind: 'url', value: m[0] });
    lastIndex = idx + m[0].length;
  }
  if (lastIndex < input.length) result.push({ kind: 'text', value: input.slice(lastIndex) });
  return result;
}
